import { get, compact, isEmpty, uniq, sumBy, divide, max, floor, isNumber } from "lodash";
import { SELECT_BACKGROUND, SelectColor } from "@treelab/panda";
import { ILookupTypeOptions } from "@treelab/common-logic";

import { COLUMN_KEYS } from "../constants/form";
import {
  XAXIS_LABEL_FONTSIZE,
  XAXIS_LABEL_LINEHEIGHT,
  XAXIS_LABEL_TEXT_ITEM,
  PIE_LABEL_WIDTH,
  PIE_LABEL_LINE_LENGTH2,
  X_AXIS_LABEL_MIN_WIDTH,
  IS_PIE_CHART,
  IS_USE_AXIS_CHART,
  IS_LINE_CHART,
} from "../constants/chart";
import { HAS_COLOR_BY_OPTION } from "../constants/tableData";

import ConvertTableData from "../class/ConvertTableData";
import CalculationDataByChart from "../class/CalculationDataByChart";

import { IChartFormValue, StatisticsType } from "../types/form-type";
import { ChartType } from "../types/global-type";
import {
  ColumnType,
  ITableColumn,
  ITableData,
  IWrokspaceData,
  ITypeOptions,
  ISelectTypeOptions,
  IStatusTypeOptions,
} from "../types/table-type";
import { ITooltipFormatterParams, IDatasetSource, IDatasetTransform } from "../types/echarts-type";

import BarChart from "../class/BarChart";
import LineChart from "../class/LineChart";
import PieChart from "../class/PieChart";
import PieRingChart from "../class/PieRingChart";
import StackBarChart from "../class/StackBarChart";

import {
  checkGroupId,
  sortDatasetByContentSort,
  sortDatasetBasic,
  convertSource,
  sortGroupDatasetByContentSort,
  convertFormValueForNot,
  getTooltipDomByChartType,
  getCalculationColumn,
  formatValueNumberToString,
  formatDimensionToString,
  getBasicDataByChartFunc,
  getLabelsWidth,
  getGridWidth,
  getGridLeft,
  getGridRight,
  getGridTop,
  getGridBottom,
  convertDateValue,
} from "./option_secondary";

import { checkValueInArr, convertColumnObj } from "./form_secondary";
import { COLORS } from "@treelab/panda";
import { isDateType } from "./tableData_secondary";
import { basicSortFunc } from "../utils/sort";

export const ChartClassByType: { [key in ChartType]?: any } = {
  [ChartType.BASIC_BAR]: BarChart,
  [ChartType.BASIC_LINE]: LineChart,
  [ChartType.BASIC_PIE]: PieChart,
  [ChartType.PIE_RING]: PieRingChart,
  [ChartType.STACKED_COLUMN_BAR]: StackBarChart,
};

/**
 * chart 核心计算逻辑
 * @param formValue
 * @param tableData
 * @param workspaceData
 * @return echarts.dataset
 */
export const getBasicDataset = (
  value: IChartFormValue,
  tableData: ITableData,
  workspaceData: IWrokspaceData,
  chartType: ChartType,
  calculation: CalculationDataByChart
): (IDatasetSource | IDatasetTransform)[] => {
  const formValue = convertFormValueForNot(convertFormValueColumnId(value, tableData), tableData);
  const { dimensionId, calculationTargetId, isShowDimensionNull, contentSort, filterMeta, isSplit, groupAtCycle } =
    formValue;

  const groupId = checkGroupId(formValue, tableData, chartType);

  const originalDimensionColumn = (get(tableData, "columns") || []).find((item) => item.id === dimensionId);
  const dimensionColumn = getRealColumn(dimensionId, tableData);
  if (!dimensionColumn || !originalDimensionColumn) return [];

  const { name: dimensionColumnName } = dimensionColumn;
  const dimensionColumnType = dimensionColumn.type;
  const calculationTargetColumnName =
    (get(tableData, "columns") || []).find((item) => item.id === calculationTargetId)?.name || "";
  const groupColumn = getRealColumn(groupId, tableData);
  const { name: groupColumnName, type: groupColumnType } = groupColumn || { name: "", type: ColumnType.TEXT };

  // check & 计算 dimensionColumn

  // ============ 新的计算方式 ============
  const keyList = compact([dimensionId, groupId]);
  const convert = new ConvertTableData({ tableData, filterMeta });
  const getBasicData = getBasicDataByChartFunc(keyList, tableData, calculationTargetId);
  let testData = convert.getBasicDataByFilter(tableData, getBasicData, true);

  // 拆分计算
  if (checkForcedSplit(formValue, tableData)) {
    testData = convert.splitArrayValueByKey(testData, dimensionId);
  }

  // 空值过滤
  if (dimensionColumnType !== ColumnType.CHECKBOX) {
    if (
      checkValueInArr(chartType, [ChartType.BASIC_LINE]) &&
      checkValueInArr(dimensionColumnType, [
        ColumnType.DATETIME,
        ColumnType.NUMBER,
        ColumnType.CURRENCY,
        ColumnType.CREATED_AT,
      ])
    ) {
      testData = calculation.filterEmpty(testData, dimensionId);
    } else if (!isShowDimensionNull) {
      testData = calculation.filterEmpty(testData, dimensionId);
    }
  } else {
    testData = calculation.filterCheckboxEmpty(testData, dimensionId);
  }

  /**
   * 时间列 value 兼容旧数据
   * fix https://app.treelab.com.cn/v2/0x1d308/0x15e9eac/rowj6SLZ2noWqmT
   */
  const dateTimeColumnIds = compact([
    isDateType(dimensionColumnType) && dimensionId,
    isDateType(groupColumnType) && groupId,
  ]);
  if (dateTimeColumnIds.length) {
    testData = convertDateValue(testData, dateTimeColumnIds);
  }

  // 开启时间粒度分组 转换 dimension column 的 value
  if (checkIsDateCycleGroup(tableData, formValue)) {
    testData = calculation.convertDimensionByDateCycle(testData, dimensionId, groupAtCycle);
  }

  // 基础转换
  testData = calculation.groupCalc(testData, keyList);
  // 添加 order
  if (dimensionColumnType === ColumnType.SELECT || (dimensionColumnType === ColumnType.MULTI_SELECT && !!isSplit)) {
    testData = calculation.setOrderBySelect(testData, dimensionId);
  }
  if (dimensionColumnType === ColumnType.STATUS) {
    testData = calculation.setOrderByStatus(testData, dimensionId);
  }

  // 饼图需要过滤 value  去除 非正数
  if (IS_PIE_CHART[chartType]) {
    testData = calculation.filterValuePositiveNumber(testData, "cellValue");
  }

  // 由于 echarts.legend 不会渲染 name 为 '' 的图例 需要手动转换成一个 tableData 中不存在的值 GROUP_EMPTY_KEY
  if (!!groupId) {
    testData = calculation.convertGroupEmpty(testData, groupId);
  }

  const isGlobalFormat = calculation.checkGlobalFormat();
  // 转换为 旧格式数据 基础数据
  const oldTestData = calculation.convertOldStructure(testData, keyList, isGlobalFormat);
  const calculationKey = "value";
  // ============ 新的计算方式 ============

  // let result =  dimensionTableData
  let result = oldTestData;

  // 进行预排序
  sortDatasetBasic(result, dimensionColumnType, dimensionColumn.typeOptions, dimensionId, !!isSplit);

  // 处理排序字段 contentSort
  if (IS_USE_AXIS_CHART[chartType]) {
    if (isEmpty(groupId)) {
      result = sortDatasetByContentSort(result, contentSort, (item: object) => get(item, calculationKey));
    } else {
      // 开启堆叠后的排序
      sortGroupDatasetByContentSort(result, contentSort, (item: object) => get(item, calculationKey), dimensionId);
    }
  }

  result = calculation.convertOldDataToSource(result);

  // 结构转换 {}[] => string[][]
  let source = convertSource(result, [dimensionId, calculationKey, groupId]);

  if (!IS_LINE_CHART[chartType]) {
    source = calculation.convertValueEmpty(source, 1);
  }

  // transform 生成 仅在开启分组时有效
  let transformList: object[] = [];
  if (!isEmpty(groupId)) {
    const keyObj: { [key in string]: boolean } = {};
    result.forEach((item) => {
      const keyName = item[groupId] as string;
      if (!get(keyObj, keyName)) keyObj[keyName] = true;
    });
    transformList = Object.keys(keyObj).map((item) => ({
      transform: {
        type: "filter",
        config: {
          dimension: groupColumnName,
          value: item,
        },
      },
    }));

    /**
     * 对 transformList 进行排序
     * 但这里的排序并没有完全实现产品的要求，因为 ECharts 会取 source[0] 来生成 x轴第一个类目
     * 但其对应的 groupColumnValue 能不是排序后 transform[0].config.value
     * 这在数据较少时 比较明显
     * 目前没有合适的解决方案
     */
    const sortFunc = basicSortFunc({
      columType: groupColumnType,
      columnOptions: groupColumn?.typeOptions as ITypeOptions,
      valueKey: "transform.config.value",
    });
    transformList.sort((item1, item2) => {
      return sortFunc(item1 as { [key in string]: string | number }, item2 as { [key in string]: string | number });
    });

    // 对于 堆叠柱状图 当 X轴 在下方时  从下到上渲染各个 transform 的结果 为了在视觉上使得 index 小的 transform 在上 这里需要做个逆序
    if (chartType === ChartType.STACKED_COLUMN_BAR) {
      transformList.reverse();
    }

    return [
      {
        dimensions: [dimensionColumnName, calculationTargetId ? calculationTargetColumnName : "count", groupColumnName],
        source: source,
      },
      // @ts-ignore
      ...transformList,
    ];
  }

  return [
    {
      dimensions: [dimensionColumnName, calculationTargetId ? calculationTargetColumnName : "count"],
      source: source,
    },
  ];
};

// 计算 x轴坐标
// 参数即是有序 source
export const getXAxisData = (list?: string[][]): string[] => {
  if (!list || !Array.isArray(list)) return [];
  const result = uniq(list.map((item) => item[0]));
  return result;
};

/**
 * 校验 columnId 是否合法 若不合法转转换成 '' 在 getBasicDataset 调用前做
 * @param value
 * @param tableData
 * @returns
 */
export const convertFormValueColumnId = (value: IChartFormValue, tableData: ITableData): IChartFormValue => {
  const checkResult: { [key in string]: string } = {};
  const columnIds = tableData.columns.map((item) => item.id);
  COLUMN_KEYS.forEach((item) => {
    const columnId = get(value, item);
    if (!columnIds.includes(columnId)) checkResult[item] = "";
  });
  const newValue = { ...value, ...checkResult };
  return newValue;
};

export const getPieTotal = (dataset: object[]): number => {
  if (!Array.isArray(dataset)) return 0;
  const source = get(dataset, "[0].source");
  if (!Array.isArray(source)) return 0;
  const total = sumBy(source, (item) => get(item, "[1]", 0));
  return total;
};

export const getBarTooltipDom = (
  params: ITooltipFormatterParams | ITooltipFormatterParams[],
  canvasWidth: number,
  chartType: ChartType,
  formatValueFunc: (value: number) => string,
  isGroup: boolean,
  isShowStackTotal?: boolean
) => {
  return getTooltipDomByChartType(params, chartType, canvasWidth, formatValueFunc, { isGroup, isShowStackTotal });
};

export const getPieTooltipDom = (
  params: ITooltipFormatterParams | ITooltipFormatterParams[],
  canvasWidth: number,
  formatValueFunc: (value: number) => string
) => {
  return getTooltipDomByChartType(params, ChartType.BASIC_PIE, canvasWidth, formatValueFunc, {});
};

export const getLineTooltipDom = (
  params: ITooltipFormatterParams | ITooltipFormatterParams[],
  canvasWidth: number,
  formatValueFunc: (value: number) => string,
  isGroup: boolean
) => {
  return getTooltipDomByChartType(params, ChartType.BASIC_LINE, canvasWidth, formatValueFunc, { isGroup });
};

/**
 * x轴 类目轴类型 label 自适应 处理逻辑
 * ECharts 的 文本溢出省略 可以保证生效后的文本长度低于 width
 * @param labelCount
 * @param canvasWidth
 * @returns
 */
export const getXAxisLabelOptionByCategory = (
  labelList: string[],
  canvasWidth: number,
  canvasHeight: number,
  isExpand: boolean,
  isShowYAxis: boolean,
  formatFunc?: (value: string) => string
) => {
  if (labelList.length === 0) return {};
  const labelCount = labelList.length;
  const gridWidth = getGridWidth(canvasWidth, isExpand, ChartType.BASIC_BAR, isShowYAxis, false);

  const basicOption = {
    fontSize: XAXIS_LABEL_FONTSIZE,
    lineHeight: XAXIS_LABEL_LINEHEIGHT,
    width: XAXIS_LABEL_TEXT_ITEM,
    overflow: "truncate",
    color: COLORS.grey,
    formatter: formatFunc,
  };

  const labelStringList = formatFunc ? labelList.map(formatFunc) : labelList;
  const labelWidthList = getLabelsWidth(labelStringList);
  const realLabelMaxWidth = max(labelWidthList) || labelWidthList[0];
  if (realLabelMaxWidth * labelCount <= gridWidth) {
    return {
      ...basicOption,
      width: realLabelMaxWidth,
      interval: 0,
    };
  } else if (X_AXIS_LABEL_MIN_WIDTH * labelCount <= gridWidth) {
    return {
      ...basicOption,
      width: X_AXIS_LABEL_MIN_WIDTH,
      interval: 0,
    };
  } else {
    const currGridWidth = getGridWidth(canvasWidth, isExpand, ChartType.BASIC_BAR, isShowYAxis, true);
    const interval = floor(divide(labelCount * (XAXIS_LABEL_LINEHEIGHT * 2), currGridWidth));
    return {
      ...basicOption,
      width: X_AXIS_LABEL_MIN_WIDTH,
      rotate: -30,
      interval: interval,
    };
  }
};

export const getFormatValueFunc = (
  tableData: ITableData,
  statisticsType: StatisticsType,
  calculationTargetId?: string
) => {
  const column = getCalculationColumn(tableData, statisticsType, calculationTargetId);
  if (!column) return (value?: number) => (isNumber(value) ? value.toFixed(0) : "");
  const formatFunc = (value: number): string => {
    return formatValueNumberToString(value, column);
  };
  return formatFunc;
};

export const getFormatDimensionFunc = (tableData: ITableData, dimensionId: string) => {
  const column = getRealColumn(dimensionId, tableData);
  if (!column) return (value: any) => `${value}`;
  const formatFunc = (value: any) => {
    return formatDimensionToString(value, column);
  };
  return formatFunc;
};

/**
 * 根据 columnId 查找 column 如果是 lookup rollup formula 则进行转换 转换成实际 column
 * 获取真实类型
 * @param columnId
 * @param tableData
 */
export const getRealColumn = (columnId: string, tableData: ITableData): ITableColumn | null => {
  const { columns } = tableData;
  const column = columns.find((item) => item.id === columnId);
  if (!column) return null;
  if (!checkValueInArr(column.type, [ColumnType.LOOKUP, ColumnType.ROLLUP, ColumnType.FORMULA])) return column;
  // 过滤 errorCode 有内容的 column
  if (!!(column.typeOptions as ITypeOptions<ColumnType.LOOKUP | ColumnType.ROLLUP | ColumnType.FORMULA>).errorCode)
    return null;
  const realColumn = convertColumnObj(column);
  return realColumn;
};

export const getOriginColumn = (tableData: ITableData, columnId?: string): ITableColumn | null => {
  if (!columnId) return null;
  const { columns } = tableData;
  const column = columns.find((item) => item.id === columnId);
  if (!column) return null;
  if (checkValueInArr(column.type, [ColumnType.LOOKUP, ColumnType.ROLLUP, ColumnType.FORMULA])) {
    if (!!(column.typeOptions as ITypeOptions<ColumnType.LOOKUP>).errorCode) return null;
  }
  return column;
};

export const getPieCenterAndRadius = (
  width: number,
  height: number,
  isShowLabel: boolean,
  isExpand: boolean,
  isShowLegend: boolean
): { center: [number, number]; radius: number } => {
  let centerX = (isExpand ? 32 : 24) + (isShowLabel ? PIE_LABEL_LINE_LENGTH2 + PIE_LABEL_WIDTH : 0);
  let centerY = isExpand ? 20 : 8;
  const drawAreaWidth =
    width - (isExpand ? 32 * 2 : 24 * 2) - (isShowLabel ? 2 * PIE_LABEL_LINE_LENGTH2 + 2 * PIE_LABEL_WIDTH : 0);
  const drawAreaHeight = height - (isExpand ? 20 + 80 : 8 + (isShowLegend ? 60 : 24));
  centerX += floor(drawAreaWidth / 2);
  centerY += floor(drawAreaHeight / 2);
  const radius = floor(Math.min(drawAreaWidth, drawAreaHeight) / 2);
  return {
    center: [centerX, centerY],
    radius,
  };
};

/**
 * 判断 是否 拆分计算
 * @param formValue
 * @param tableData
 */
export const checkForcedSplit = (formValue: IChartFormValue, tableData: ITableData): boolean => {
  const { isSplit, dimensionId, isGroupAtCycle, chartType } = formValue;
  if (isSplit) return true;
  const dimensionColumn = getRealColumn(dimensionId, tableData);
  if (!dimensionColumn) return false;
  const { type } = dimensionColumn;
  if (isDateType(type) && isGroupAtCycle) return true;
  if (
    chartType === ChartType.BASIC_LINE &&
    checkValueInArr(type, [
      ColumnType.NUMBER,
      ColumnType.RATING,
      ColumnType.CURRENCY,
      ColumnType.DATETIME,
      ColumnType.CREATED_AT,
    ])
  )
    return true;
  return false;
};

export const getGridClientRect = (
  isExpand: boolean,
  chartType: ChartType,
  isLabelRotate: boolean,
  isShowYAxis: boolean,
  isShowLegend: boolean
): { top: number; bottom: number; left: number; right: number } => {
  const result = {
    top: getGridTop(isExpand, chartType, isShowLegend),
    left: getGridLeft(isExpand, chartType, isShowYAxis),
    right: getGridRight(isExpand, chartType, isLabelRotate),
    bottom: getGridBottom(isExpand, chartType, isLabelRotate),
  };
  return result;
};

export const checkIsDateCycleGroup = (tableData: ITableData, formValue: IChartFormValue): boolean => {
  const { dimensionId, isGroupAtCycle } = formValue;
  if (!isGroupAtCycle) return false;
  const dimensionColumnType = getRealColumn(dimensionId, tableData)?.type;
  if (!!dimensionColumnType && isDateType(dimensionColumnType)) return true;
  return false;
};

export const getTooltipPosition = (
  point: [number, number],
  params: ITooltipFormatterParams | ITooltipFormatterParams[],
  dom: HTMLElement,
  rect: Object,
  size: { contentSize: [number, number]; viewSize: [number, number] }
) => {
  const { contentSize, viewSize } = size;
  const [pointX, pointY] = point;
  let left: number;
  let top: number;
  if (viewSize[0] - pointX > contentSize[0]) {
    left = pointX;
  } else if (pointX > contentSize[0]) {
    left = pointX - contentSize[0];
  } else {
    left = pointX;
  }
  if (viewSize[1] - pointY > contentSize[1]) {
    top = pointY;
  } else if (pointY > contentSize[1]) {
    top = pointY - contentSize[1];
  } else {
    top = pointY;
  }
  return [left, top];
};

/**
 * 根据 option 获取 optionId 和 color map
 */
export const getOptionColorByValue = ({
  columns,
  columnId,
}: {
  columns: ITableColumn[];
  columnId: string;
}): Record<string, string> => {
  let result = {};
  const column = columns.find((column) => column.id === columnId);
  if (!column) {
    console.error("找不到对应 column", columnId);
    return result;
  }

  if (!HAS_COLOR_BY_OPTION[column.type]) {
    console.error("该列类型没有 option.color ", column?.type);
    return result;
  }

  const getColorMap = (type: ColumnType, typeOptions: ITypeOptions): Record<string, string> => {
    switch (type) {
      case ColumnType.SELECT:
      case ColumnType.MULTI_SELECT:
        return (typeOptions as ISelectTypeOptions)
          .options
          .reduce((acc, curr) => ({ ...acc, [curr.optionId]: SELECT_BACKGROUND[curr.color as SelectColor], }), {});
      case ColumnType.STATUS:
        return (typeOptions as IStatusTypeOptions)
          .statuses
          .reduce((acc, curr) => ({ ...acc, [curr.statusId]: SELECT_BACKGROUND[curr.color as SelectColor], }), {});
      case ColumnType.LOOKUP:
        const { lookupColumnType, lookupTypeOptions } = typeOptions as ILookupTypeOptions;
        return getColorMap(lookupColumnType as ColumnType, lookupTypeOptions as ITypeOptions);
    }
    return {};
  }
  return getColorMap(column.type, column.typeOptions);
};

export const checkColumnHasColor = ({ columnId, tableData }: { columnId?: string; tableData: ITableData }): boolean => {
  if (!columnId) return false;
  const column = getRealColumn(columnId, tableData);
  if (!column) return false;

  return HAS_COLOR_BY_OPTION[column.type];
};

export const showUseOptionColorSwitch = ({
  formValue,
  tableData,
}: {
  formValue: IChartFormValue;
  tableData: ITableData;
}): boolean => {
  const { chartType } = formValue;
  const groupId = checkGroupId(formValue, tableData, chartType);
  if (chartType === ChartType.BASIC_LINE && !groupId) {
    return false;
  }
  return checkColumnHasColor({ columnId: groupId || formValue.dimensionId, tableData });
};
